import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { mockRfqManagementService } from '@/services/mock/MockRfqManagementService';
import { RfqFilterState, RfqMetricsSummary, RfqRecord, RfqStatusKey } from '@/types/domain/rfq-management';
import { RfqKpiCards } from '@/components/rfq/RfqKpiCards';
import { DataTable, ColumnDef } from '@/components/data-display/DataTable';
import { RfqExtendDeadlineModal } from '@/components/rfq/RfqExtendDeadlineModal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import {
  Layers,
  Plus,
  RefreshCw,
  Search,
  Eye,
  Pencil,
  Trash2,
  Building2,
  Users,
  AlertTriangle,
  Loader2,
  X,
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { formatDate } from '@/lib/utils';
import { SlideOverDrawer } from '@/components/ui/SlideOverDrawer';
import { RfqDetailPage } from './RfqDetailPage';

const formatInr = (val: number) =>
  `₹${Number(val || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const renderRfqStatusBadge = (status: RfqStatusKey | string) => {
  let color = 'bg-primary/10 text-primary border-primary/20';
  let dot = 'bg-primary';
  let label = status.replace(/_/g, ' ');

  switch (status) {
    case 'OPEN':
      color = 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20';
      dot = 'bg-blue-500';
      label = 'Open Sourcing';
      break;
    case 'RESPONSES_RECEIVED':
      color = 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20';
      dot = 'bg-emerald-500';
      label = 'Bids Received';
      break;
    case 'PARTIALLY_RESPONDED':
    case 'INVITATIONS_SENT':
      color = 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20';
      dot = 'bg-amber-500';
      label = 'Awaiting Quotes';
      break;
    case 'DEADLINE_APPROACHING':
      color = 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20';
      dot = 'bg-rose-500';
      label = 'Closing Soon';
      break;
    case 'EVALUATION_READY':
      color = 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20';
      dot = 'bg-purple-500';
      label = 'Evaluation Ready';
      break;
    case 'DRAFT':
      color = 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20';
      dot = 'bg-slate-400';
      label = 'Draft Package';
      break;
    case 'CLOSED':
      color = 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20';
      dot = 'bg-zinc-500';
      label = 'Closed';
      break;
    case 'CANCELLED':
      color = 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20';
      dot = 'bg-rose-500';
      label = 'Cancelled';
      break;
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-2xs font-poppins whitespace-nowrap w-36 justify-center ${color}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dot} shrink-0`} />
      <span className="truncate">{label}</span>
    </span>
  );
};

export const RfqListPage: React.FC = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<RfqMetricsSummary | null>(null);

  // Extend Deadline modal state
  const [extendRfqId, setExtendRfqId] = useState<string | null>(null);
  const [isExtending, setIsExtending] = useState(false);

  // Deletion state
  const [rfqToDelete, setRfqToDelete] = useState<RfqRecord | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedRfqForDrawer, setSelectedRfqForDrawer] = useState<string | null>(null);

  const [filters, setFilters] = useState<RfqFilterState>({
    search: '',
    status: 'ALL',
    priority: 'ALL',
    category: 'ALL',
    department: 'ALL',
    plant: 'ALL',
    supplierStatus: 'ALL',
    page: 1,
    pageSize: 25,
  });

  const fetchMetrics = useCallback(async () => {
    const m = await mockRfqManagementService.getRfqMetrics();
    setMetrics(m);
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Lock body scroll when delete modal is open
  useEffect(() => {
    if (!rfqToDelete) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = orig;
    };
  }, [rfqToDelete]);

  const fetchFn = useCallback(
    async (params: { page?: number; pageSize?: number }) => {
      const res = await mockRfqManagementService.getRfqRecords({
        ...filters,
        page: params.page || 1,
        pageSize: params.pageSize || 25,
      });
      return {
        data: res.items,
        page: params.page || 1,
        pageSize: params.pageSize || 25,
        total: res.total,
        totalPages: Math.ceil(res.total / (params.pageSize || 25)),
        hasNextPage: (params.page || 1) * (params.pageSize || 25) < res.total,
        hasPreviousPage: (params.page || 1) > 1,
      };
    },
    [filters]
  );

  const {
    items: records,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    total,
    sentinelRef,
    retry,
  } = useInfiniteScroll<RfqRecord>({
    fetchFn,
    params: { ...filters },
    pageSize: 25,
  });

  const handleConfirmExtend = async (newClosingDate: string, reason: string) => {
    if (!extendRfqId) return;
    try {
      setIsExtending(true);
      await mockRfqManagementService.extendRfqDeadline(extendRfqId, newClosingDate, reason);
      setExtendRfqId(null);
      retry();
      fetchMetrics();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to extend deadline');
    } finally {
      setIsExtending(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!rfqToDelete) return;
    const targetId = rfqToDelete.id;
    try {
      setIsDeleting(true);
      await mockRfqManagementService.deleteRfq(targetId);
      setRfqToDelete(null);
      retry();
      fetchMetrics();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to delete RFQ.');
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: ColumnDef<RfqRecord>[] = [
    {
      header: 'RFQ Number',
      accessorKey: 'rfqNumber',
      sortable: true,
      cell: (row) => (
        <div className="space-y-0.5 font-poppins">
          <span
            onClick={() => navigate(ROUTES.RFQ.DETAILS(row.id))}
            className="font-poppins font-bold text-primary hover:underline cursor-pointer whitespace-nowrap text-sm block"
          >
            {row.rfqNumber}
          </span>
          <span className="text-xs text-foreground-muted font-mono block whitespace-nowrap">
            {formatDate(row.closingDate)} • REQ: {row.sourceRequestId}
          </span>
        </div>
      ),
    },
    {
      header: 'Sourcing Scope & Title',
      accessorKey: 'title',
      cell: (row) => (
        <div className="flex flex-col min-w-0 max-w-[190px] xl:max-w-[220px] font-poppins">
          <span
            className="font-bold text-foreground text-sm truncate block"
            title={row.title}
          >
            {row.title}
          </span>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-xs text-foreground-secondary truncate block">
              {row.category}
            </span>
            <Badge
              variant={row.priority === 'CRITICAL' ? 'destructive' : 'outline'}
              className="text-[10px] px-1.5 py-0 font-bold"
            >
              {row.priority}
            </Badge>
          </div>
        </div>
      ),
    },
    {
      header: 'Department & Plant',
      accessorKey: 'departmentName',
      cell: (row) => (
        <div className="max-w-[130px] xl:max-w-[160px] truncate font-poppins" title={row.departmentName}>
          <span className="text-sm font-semibold text-foreground truncate block flex items-center gap-1">
            <Building2 className="h-3.5 w-3.5 text-primary shrink-0" />
            {row.departmentName}
          </span>
          <span className="text-xs text-foreground-muted font-mono block">
            {row.plantCode}
          </span>
        </div>
      ),
    },
    {
      header: 'Supplier Bids',
      cell: (row) => {
        const totalSuppliers = row.suppliers.length;
        const responded = row.suppliers.filter((s) => s.invitationStatus === 'RESPONDED').length;
        return (
          <div className="font-poppins space-y-1">
            <div className="flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5 text-primary shrink-0" />
              <span className="text-xs font-bold text-foreground font-mono">
                {responded} / {totalSuppliers} Bids
              </span>
            </div>
            <span className="text-[11px] text-foreground-muted block">
              {responded === totalSuppliers && totalSuppliers > 0 ? '100% Quotes In' : 'Bidding in progress'}
            </span>
          </div>
        );
      },
    },
    {
      header: 'Estimated Budget',
      accessorKey: 'estimatedTotalAmount',
      cell: (row) => (
        <div className="font-poppins whitespace-nowrap">
          <span className="font-bold text-foreground font-mono text-sm block">
            {formatInr(row.estimatedTotalAmount)}
          </span>
          <span className="text-[11px] text-foreground-muted block font-mono">
            INR Target
          </span>
        </div>
      ),
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: (row) => (
        <div className="flex items-center font-poppins">
          {renderRfqStatusBadge(row.status)}
        </div>
      ),
    },
    {
      header: 'Actions',
      cell: (row) => (
        <div
          className="flex items-center gap-1.5 justify-start shrink-0 whitespace-nowrap font-poppins min-w-[110px]"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedRfqForDrawer(row.id)}
            title="View RFQ Workspace (Slide-over)"
            className="h-8 w-8 p-0 rounded-lg hover:bg-primary/15 hover:text-primary active:scale-90 transition-all duration-200 cursor-pointer text-foreground-secondary shrink-0 inline-flex items-center justify-center group shadow-2xs hover:shadow-xs"
          >
            <Eye className="h-4 w-4 transition-transform duration-200 group-hover:scale-115" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(ROUTES.RFQ.EDIT(row.id))}
            title="Edit Sourcing Package"
            className="h-8 w-8 p-0 rounded-lg hover:bg-primary/10 cursor-pointer text-primary hover:text-primary-dark shrink-0 inline-flex items-center justify-center"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setRfqToDelete(row)}
            title="Delete RFQ"
            className="h-8 w-8 p-0 rounded-lg text-rose-500 hover:text-rose-600 hover:bg-rose-500/15 cursor-pointer shrink-0 inline-flex items-center justify-center"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 pb-12 font-poppins">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-2.5 font-poppins">
            <Layers className="h-7 w-7 text-primary" /> Strategic RFQ & Sourcing Workspace
          </h1>
          <p className="text-xs text-foreground-secondary font-medium mt-1 font-poppins">
            Competitive supplier bidding packages, technical specifications, and commercial price quotes.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              fetchMetrics();
              retry();
            }}
            className="rounded-xl font-bold font-poppins cursor-pointer"
          >
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>

          <Button
            onClick={() => navigate(ROUTES.RFQ.NEW)}
            className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground font-poppins cursor-pointer shadow-xs"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Create New RFQ
          </Button>
        </div>
      </div>

      {/* KPI Metric Summary Strip */}
      {metrics && (
        <RfqKpiCards
          metrics={metrics}
          onFilterClick={(type) => {
            setFilters((prev) => ({
              ...prev,
              status: type === 'ALL' ? 'ALL' : type,
            }));
          }}
        />
      )}

      {/* Main Ledger Card with Squeezed Status Tabs & Search */}
      <Card className="border-border rounded-2xl shadow-xs overflow-hidden font-poppins">
        <CardHeader className="p-4 border-b border-border/60 bg-muted/20">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            {/* Squeezed Status Tabs */}
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { key: 'ALL', label: 'All Sourcing' },
                { key: 'OPEN', label: 'Open' },
                { key: 'RESPONSES_RECEIVED', label: 'Bids Received' },
                { key: 'EVALUATION_READY', label: 'Evaluation' },
                { key: 'DEADLINE_APPROACHING', label: 'Closing Soon' },
                { key: 'DRAFT', label: 'Draft' },
              ].map((tab) => {
                const isActive = filters.status === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setFilters((prev) => ({ ...prev, status: tab.key }))}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer font-poppins ${
                      isActive
                        ? 'bg-primary text-primary-foreground font-bold shadow-xs'
                        : 'bg-card border border-border text-foreground-secondary hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Search Input */}
            <div className="relative min-w-[240px]">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                placeholder="Search RFQ #, package, category..."
                className="w-full h-9 pl-9 pr-3 text-xs font-medium rounded-xl border border-input bg-background text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary font-poppins"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-4 overflow-x-hidden">
          <DataTable
            data={records}
            columns={columns}
            isLoading={isLoading}
            pageSize={25}
            totalCount={total}
            infiniteScroll={true}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            sentinelRef={sentinelRef}
            searchPlaceholder="Search RFQ packages..."
            fixedHeight={true}
            containerHeightClass="max-h-[580px] overflow-y-auto overflow-x-hidden"
            emptyTitle="No RFQ Packages Found"
            emptyDescription="There are no sourcing packages matching your current filter criteria."
            onRowClick={(row) => navigate(ROUTES.RFQ.DETAILS(row.id))}
          />
        </CardContent>
      </Card>

      {/* Extend Deadline Modal */}
      {extendRfqId && (
        <RfqExtendDeadlineModal
          isOpen={!!extendRfqId}
          onClose={() => setExtendRfqId(null)}
          currentClosingDate="2026-10-31"
          isSubmitting={isExtending}
          onConfirm={handleConfirmExtend}
        />
      )}

      {/* Delete Confirmation Modal (Mounted via createPortal to document.body) */}
      {rfqToDelete &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-in fade-in"
            onClick={() => !isDeleting && setRfqToDelete(null)}
          >
            <div
              className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl space-y-4 font-poppins animate-in zoom-in-95 my-auto text-foreground"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-border/60 pb-3">
                <div className="flex items-center gap-2 text-rose-600">
                  <AlertTriangle className="h-5 w-5" />
                  <h3 className="text-base font-bold text-foreground font-poppins">
                    Delete Sourcing Package?
                  </h3>
                </div>
                <button
                  onClick={() => !isDeleting && setRfqToDelete(null)}
                  className="rounded-lg p-1 hover:bg-muted text-foreground-muted cursor-pointer"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Package Details Box */}
              <div className="rounded-xl bg-muted/40 border border-border p-3.5 space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    RFQ Reference
                  </span>
                  <span className="font-mono font-bold text-primary">
                    {rfqToDelete.rfqNumber}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Sourcing Title
                  </span>
                  <span className="font-bold text-foreground truncate max-w-[200px] text-right">
                    {rfqToDelete.title}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Estimated Target
                  </span>
                  <span className="font-bold text-foreground font-mono">
                    {formatInr(rfqToDelete.estimatedTotalAmount)}
                  </span>
                </div>
              </div>

              <p className="text-xs text-foreground-secondary leading-relaxed">
                Are you sure you want to delete this Request for Quotation? This action removes the bidding package and cancels all open supplier invitations.
              </p>

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-border/60">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isDeleting}
                  onClick={() => setRfqToDelete(null)}
                  className="cursor-pointer font-bold rounded-xl font-poppins"
                >
                  Cancel
                </Button>
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={handleConfirmDelete}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 active:scale-95 text-white font-extrabold text-xs shadow-lg shadow-rose-600/30 transition-all cursor-pointer disabled:opacity-50 font-poppins"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-3.5 w-3.5" />
                      <span>Delete RFQ</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Right Slide-Over Curtain Drawer for RFQ Workspace */}
      <SlideOverDrawer
        isOpen={Boolean(selectedRfqForDrawer)}
        onClose={() => setSelectedRfqForDrawer(null)}
        title="RFQ Sourcing Package Dossier"
        subtitle="Bidder invitations, technical specifications, received quotations, and commercial evaluation"
        fullPageUrl={selectedRfqForDrawer ? ROUTES.RFQ.DETAILS(selectedRfqForDrawer) : undefined}
      >
        {selectedRfqForDrawer && (
          <RfqDetailPage
            recordId={selectedRfqForDrawer}
            isDrawer={true}
            onClose={() => setSelectedRfqForDrawer(null)}
          />
        )}
      </SlideOverDrawer>
    </div>
  );
};
